import { TheWestSection } from '@news-mono/common'
import {
    Block,
    BreachEvent,
    CardEvents,
    CollectionEvent,
    LinkClickedEvent,
    PlayerEvent,
    PublicationType,
    RequiredAccess,
    useFeature,
} from '@news-mono/web-common'
import { TheWestSections } from '@west-australian-newspapers/publication-types'
import H from 'history'
import React from 'react'
import { Logger } from 'typescript-log'
import { useFeatureForSection } from '../../../../feature-toggling'
import {
    useActiveExperiment,
    useVariantId,
} from '../../abtesting/AbTestingProvider'
import {
    BASE_50_DEAL_OFFERCODE,
    useUpdateBreachWallContext,
} from '../../common'
import { showBreachForTopic } from '../../helpers'
import { BreachWallContentType } from '../../helpers/BreachWallTypes'
import { ArticlePreview } from './ArticlePreview/ArticlePreview'
import { BlackFridayBreachNotification } from './BlackFridayBreachNotification/BlackFridayBreachNotification'
import { BreachNotification } from './BreachNotification/BreachNotification'
import { SportBreachNotification } from './SportBreachNotification/SportBreachNotification'

export interface BreachNotificationWrapperProps {
    publication: PublicationType
    breachDeal: string
    breachCreativeName: string
    requiredAccess: RequiredAccess
    onEvent: (
        event:
            | BreachEvent
            | PlayerEvent
            | LinkClickedEvent
            | CardEvents
            | CollectionEvent,
    ) => void
    type: BreachWallContentType
    // Optional properties that can be passed on a per-campaign basis
    contentOptions?: {
        [key: string]: any
    }
    blocks: Block[]
    log: Logger
}

export type BreachWallOptions = {
    canShowSportBreach: boolean
    canShowBreachDiscount50afl: boolean
    canShowBreachDiscount50sch: boolean
    canShowBreachDiscount190Year: boolean
    canShowBreachMatildas: boolean
    canShowBlackFriday: boolean
    canShowBreachDiscountGeneric: boolean
    canShowEofyDiscount: boolean
    canShowBusinessPackDiscount: boolean
    canShowSportPackDiscount: boolean
    canShowBaliBombingDiscount: boolean
}

export function useBreachWallConfig(
    location: H.Location,
    section: TheWestSection,
) {
    const canShowAbTests = useFeature('breach-ab-testing')
    const activeExperiment = useActiveExperiment(
        section,
        location,
        canShowAbTests,
    )
    const variant = useVariantId(activeExperiment)

    const options: BreachWallOptions = {
        canShowSportBreach: useFeature('sport-breach-screen-variant'),
        canShowBreachDiscount50sch: useFeature('top-50-schools'),
        canShowBreachDiscount190Year: useFeature('breach-190-year'),
        canShowBlackFriday: useFeature('black-friday'),
        canShowBreachDiscountGeneric: useFeature('50-off-breach-discount'),
        canShowEofyDiscount: useFeature('subs-campaign-eofy-weekend-digital'),
        canShowBusinessPackDiscount: useFeature(
            'subs-campaign-business-pack-2022',
        ),
        canShowSportPackDiscount: useFeature('subs-campaign-sport-pack-2022'),
        canShowBaliBombingDiscount: useFeature(
            'subs-campaign-bali-bombing-2022',
        ),
        canShowBreachDiscount50afl: useFeature('50-off-afl-breach-screen'),
        canShowBreachMatildas: useFeature('breach-matildas-offer'),
    }

    // Check AB Testing before trying to return the normal breach walls...
    if (canShowAbTests) {
        // Experiment was found and is active! :D
        if (activeExperiment !== undefined && typeof(activeExperiment) !== 'undefined' && variant !== undefined) {
            return activeExperiment.getBreachWallConfig(variant)
        }
    }

    return getBreachWallContentType(location, options)
}

export function getBreachWallContentType(
    location: H.Location,
    options: BreachWallOptions,
): {
    deal: string
    creativeName: string
    type: BreachWallContentType
} {
    if (options.canShowBlackFriday) {
        return {
            creativeName: 'TW_Article_Breach_Black_Friday_1',
            deal: 'BLKFRI1',
            type: 'black-friday',
        }
    }

    // Multiple AFL50 subtopic targets
    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50afl,
            '*',
            'afl',
            true,
            true,
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'AFL50',
            type: '50-off-afl-breach-screen',
        }
    }
    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50afl,
            '*',
            'aflw',
            true,
            true,
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'AFL50',
            type: '50-off-afl-breach-screen',
        }
    }
    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50afl,
            '*',
            'wafl',
            true,
            true,
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'AFL50',
            type: '50-off-afl-breach-screen',
        }
    }
    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50afl,
            '*',
            'wafl-colts',
            true,
            true,
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'AFL50',
            type: '50-off-afl-breach-screen',
        }
    }
    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50afl,
            '*',
            'psa-footy',
            true,
            true,
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'AFL50',
            type: '50-off-afl-breach-screen',
        }
    }
    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50afl,
            '*',
            'west-coast-eagles',
            true,
            true,
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'AFL50',
            type: '50-off-afl-breach-screen',
        }
    }
    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50afl,
            '*',
            'fremantle-dockers',
            true,
            true,
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'AFL50',
            type: '50-off-afl-breach-screen',
        }
    }

    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50sch,
            '*',
            'top-50-schools',
            true,
            true,
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'SCHOOL30',
            type: 'top-50-schools',
        }
    }

    if (options.canShowBreachDiscount190Year) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: '190BD23',
            type: 'breach-190-year',
        }
    }

    if (options.canShowBreachMatildas) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'MATI23',
            type: 'matildas-offer',
        }
    }

    if (options.canShowBreachDiscountGeneric) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: BASE_50_DEAL_OFFERCODE,
            type: '50-off-breach-discount',
        }
    }

    if (options.canShowEofyDiscount) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'EOFY6',
            type: 'eofy',
        }
    }

    if (
        showBreachForTopic(
            location,
            options.canShowBusinessPackDiscount,
            'business',
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'BUSN2',
            type: 'business-pack',
        }
    }

    if (
        showBreachForTopic(location, options.canShowSportPackDiscount, 'sport')
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'SP3022',
            type: 'sport-pack',
        }
    }

    if (showBreachForTopic(location, options.canShowSportBreach, 'sport')) {
        return {
            creativeName: 'TW_Article_Breach_Article_SPORT_PACKAGE',
            deal: 'sportspack',
            type: 'sport',
        }
    }

    if (
        showBreachForTopic(
            location,
            options.canShowBaliBombingDiscount,
            '*',
            'bali-bombings',
            true,
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'BB2022',
            type: 'bali-bombing',
        }
    }

    return {
        creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
        deal: 'default',
        type: 'default',
    }
}

export const BreachNotificationWrapper: React.FC<BreachNotificationWrapperProps> =
    ({
        publication,
        breachDeal,
        breachCreativeName,
        requiredAccess,
        onEvent,
        type,
        contentOptions,
        blocks,
        log,
    }) => {
        const showLiveSportTrailer =
            contentOptions && contentOptions.showLiveSportHubTrailer

        const showBanner = useFeature('breachwall-banner')

        useUpdateBreachWallContext({
            publication,
            breachCreativeName,
            breachDeal,
            requiredAccessLevel: requiredAccess.level,
        })

        const showPartOfArticle = useFeatureForSection({
            feature: 'tp-subscription-breach-show-part-article',
            sections: TheWestSections.default,
        })

        switch (type) {
            case 'sport':
                return (
                    <SportBreachNotification
                        breachDeal={breachDeal}
                        breachCreativeName={breachCreativeName}
                        breachType={type}
                        onEvent={onEvent}
                    />
                )
            case 'black-friday':
                return (
                    <BlackFridayBreachNotification
                        breachDeal={breachDeal}
                        breachCreativeName={breachCreativeName}
                        breachType={type}
                        onEvent={onEvent}
                    />
                )
            default:
                return (
                    <>
                        {showPartOfArticle && (
                            <ArticlePreview
                                blocks={blocks}
                                numberOfBlocksToPreview={2}
                                log={log}
                                onEvent={onEvent}
                            />
                        )}
                        <BreachNotification
                            breachDeal={breachDeal}
                            breachCreativeName={breachCreativeName}
                            onEvent={onEvent}
                            type={type}
                            contentOptions={{
                                showLiveSportTrailer,
                                showBanner,
                            }}
                        />
                    </>
                )
        }
    }

BreachNotificationWrapper.displayName = 'BreachNotificationWrapper'

export default BreachNotificationWrapper
